import { ImportingText } from 'owa-locstrings/lib/strings/importingtext.locstring.json';
import { ICSFileErrorText } from 'owa-locstrings/lib/strings/icsfileerrortext.locstring.json';
import { AddToCalendarSuccessMessgaeWithIcon } from 'owa-locstrings/lib/strings/addtocalendarsuccessmessgaewithicon.locstring.json';
import { ConsumerDefaultSaveToCloudLocationText } from 'owa-locstrings/lib/strings/consumerdefaultsavetocloudlocationtext.locstring.json';
import {
    SavingOneFileToGroupFilesText,
    SavingOneFileToOneDriveConsumerText,
    SavingOneFileToOneDriveText,
    SavedOneFileToGroupFilesText,
    SavedOneFileToOneDriveText,
    FailedToSaveFileToGroupFilesText,
    FailedToSaveFileToOneDriveText,
    UploadingToGroupFilesText,
    UploadingToOneDriveConsumerText,
    UploadingToOneDriveText,
    CanceledSaveOneFileToOneDriveText,
    UpdatingAttachmentText,
    DeletingAttachmentText,
    CancelingText,
    AttachingACopyText,
    FailedToAttachAsACopyText,
} from './getAttachmentInfoSecondLineInfo.locstring.json';
import { OneDriveText } from 'owa-locstrings/lib/strings/onedrivetext.locstring.json';
import loc, { format } from 'owa-localize';
import { isAttachmentExpired } from './isAttachmentExpired';
import { assertNever } from 'owa-assert';
import { getCompanyName } from 'owa-attachment-data';
import { isFeatureEnabled } from 'owa-feature-flags';
import { ActionMessageId, AttachmentFullViewState } from 'owa-attachment-full-data';
import { AttachmentModel } from 'owa-attachment-model-store';
import { MailboxType } from 'owa-client-ids';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';

import {
    getSecondLineTextAndTitle,
    TextAndTitle,
} from 'owa-attachment-view/lib/utils/getAttachmentInfo';

export interface AttachmentInfoSecondLineInfo {
    text: string | null;
    title: string | null;
    shouldDisplaySuccessIcon: boolean;
    shouldDisplayFailureIcon: boolean;
    useExpiredAttachmentStyling: boolean;
}

function getSaveToCloudInProgressText(isConsumer: boolean, isGroup: boolean): string {
    if (isGroup) {
        return loc(SavingOneFileToGroupFilesText);
    }

    const companyName = getCompanyName();
    return isConsumer
        ? format(
              loc(SavingOneFileToOneDriveConsumerText),
              loc(ConsumerDefaultSaveToCloudLocationText)
          )
        : format(loc(SavingOneFileToOneDriveText), companyName);
}

function getSaveToCloudCompletedText(isConsumer: boolean, isGroup: boolean): string {
    if (isGroup) {
        return loc(SavedOneFileToGroupFilesText);
    }

    return format(
        loc(SavedOneFileToOneDriveText),
        isConsumer ? loc(ConsumerDefaultSaveToCloudLocationText) : loc(OneDriveText)
    );
}

function getSaveToCloudFailedText(isConsumer: boolean, isGroup: boolean): string {
    if (isGroup) {
        return loc(FailedToSaveFileToGroupFilesText);
    }

    return format(
        loc(FailedToSaveFileToOneDriveText),
        isConsumer ? loc(ConsumerDefaultSaveToCloudLocationText) : loc(OneDriveText)
    );
}

function getConvertToClassicToCloudInProgressText(isConsumer: boolean, isGroup: boolean): string {
    if (isGroup) {
        return loc(UploadingToGroupFilesText);
    }

    const companyName = getCompanyName();
    return isConsumer
        ? format(
              loc(UploadingToOneDriveConsumerText),
              companyName,
              loc(ConsumerDefaultSaveToCloudLocationText)
          )
        : format(loc(UploadingToOneDriveText), companyName);
}

function getDefaultSecondLineInfo(
    attachmentViewState: AttachmentFullViewState,
    attachment: AttachmentModel
): AttachmentInfoSecondLineInfo {
    const textAndTitle: TextAndTitle = getSecondLineTextAndTitle(attachmentViewState, attachment);
    return {
        text: textAndTitle.text,
        title: textAndTitle.title,
        shouldDisplaySuccessIcon: false,
        shouldDisplayFailureIcon: false,
        useExpiredAttachmentStyling:
            isFeatureEnabled('doc-attachment-referenceAttachmentExpiration') &&
            isAttachmentExpired(attachment),
    };
}

export default function getAttachmentInfoSecondLineInfo(
    attachmentViewState: AttachmentFullViewState,
    attachment: AttachmentModel
): AttachmentInfoSecondLineInfo {
    const isConsumerUser: boolean = isConsumer();
    const isGroup: boolean =
        attachment.id.mailboxInfo !== undefined &&
        attachment.id.mailboxInfo.type == MailboxType.GroupMailbox;
    if (attachmentViewState.actionMessage === ActionMessageId.None) {
        return getDefaultSecondLineInfo(attachmentViewState, attachment);
    }

    let text: string;
    let shouldDisplaySuccessIcon: boolean = false;
    let shouldDisplayFailureIcon: boolean = false;
    switch (attachmentViewState.actionMessage) {
        case ActionMessageId.SaveToCloudInProgress:
            text = getSaveToCloudInProgressText(isConsumerUser, isGroup);
            break;
        case ActionMessageId.SaveToCloudCanceled:
            text = loc(CanceledSaveOneFileToOneDriveText);
            break;
        case ActionMessageId.SaveToCloudCompleted:
            text = getSaveToCloudCompletedText(isConsumerUser, isGroup);
            break;
        case ActionMessageId.SaveToCloudCompletedWithIcon:
            text = getSaveToCloudCompletedText(isConsumerUser, isGroup);
            shouldDisplaySuccessIcon = true;
            break;
        case ActionMessageId.SaveToCloudFailedWithIcon:
        case ActionMessageId.ConvertClassicToCloudyFailedWithIcon:
            text = getSaveToCloudFailedText(isConsumerUser, isGroup);
            shouldDisplayFailureIcon = true;
            break;
        case ActionMessageId.ChangePermissionInProgress:
            text = loc(UpdatingAttachmentText);
            break;
        case ActionMessageId.DeleteInProgress:
            text = loc(DeletingAttachmentText);
            break;
        case ActionMessageId.ConvertClassicToCloudyInProgress:
            text = getConvertToClassicToCloudInProgressText(isConsumerUser, isGroup);
            break;
        case ActionMessageId.Canceling:
            text = loc(CancelingText);
            break;
        case ActionMessageId.ConvertCloudyToClassicInProgress:
            text = loc(AttachingACopyText);
            break;
        case ActionMessageId.ConvertCloudyToClassicFailedWithIcon:
            text = loc(FailedToAttachAsACopyText);
            shouldDisplayFailureIcon = true;
            break;
        case ActionMessageId.ImportToCalendarCanceled:
            text = loc(CancelingText);
            break;
        case ActionMessageId.ImportToCalendarCompletedWithIcon:
            text = loc(AddToCalendarSuccessMessgaeWithIcon);
            shouldDisplaySuccessIcon = true;
            break;
        case ActionMessageId.ImportToCalendarFailedWithIcon:
            text = loc(ICSFileErrorText);
            shouldDisplayFailureIcon = true;
            break;
        case ActionMessageId.ImportToCalendarInProgress:
            text = loc(ImportingText);
            break;
        case ActionMessageId.RefreshInProgress:
            text = loc(UpdatingAttachmentText);
            break;
        default:
            assertNever(attachmentViewState.actionMessage);
    }
    return {
        text: text,
        title: text,
        shouldDisplaySuccessIcon: shouldDisplaySuccessIcon,
        shouldDisplayFailureIcon: shouldDisplayFailureIcon,
        useExpiredAttachmentStyling: false,
    };
}
